import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { requestReset, confirmReset } from '../services/studentAuthApi';

// BP 26 / WIP-002 — /reset (public — no session required). Two channels share
// one confirm form: an emailed code (?sid&code prefilled from the link in the
// email) or an instructor-issued code (the student types both by hand after
// their instructor reads it out). The DEFAULT flow in practice is the
// instructor-mediated one — /auth/reset/request only does anything when an
// email provider is configured, but it always 202s either way (no
// enumeration), so the UI still never claims to know whether THIS student's
// email went out. It DOES know — from the response's global, per-student-
// blind `email_configured` flag — whether any provider is active at all, and
// switches its message rather than implying a link was sent when nothing can
// send one (the dev_log-shows-"sent" problem WIP-002 exists to fix).
type View = 'start' | 'requested' | 'confirm' | 'done';

function messageForConfirmError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string; message?: string } | undefined;
    if (data?.error === 'invalid_or_expired_code') {
      return 'That code is invalid, expired, or already used. Ask your instructor for a new one, or request another email.';
    }
    if (data?.error === 'weak_password') return data.message || 'That password is not strong enough.';
    if (data?.error === 'invalid_body') return 'Please check your student ID and code.';
  }
  return 'Could not reset your password. Please try again.';
}

const STUDENT_ID_RE = /^\d{9}$/;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const prefillSid = params.get('sid') || '';
  const prefillCode = params.get('code') || '';

  const [view, setView] = useState<View>(prefillCode ? 'confirm' : 'start');

  // Request-by-email form
  const [reqStudentId, setReqStudentId] = useState('');
  const [reqBusy, setReqBusy] = useState(false);
  const [reqError, setReqError] = useState<string | null>(null);
  // WIP-002 — whether email delivery is actually configured, so the
  // "requested" view can stop implying a link was sent when it wasn't.
  const [emailConfigured, setEmailConfigured] = useState(true);

  // Confirm form
  const [studentId, setStudentId] = useState(prefillSid);
  const [code, setCode] = useState(prefillCode);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const inputClass =
    'w-full rounded-md border border-ws-border bg-ws-surface px-3 py-2 text-sm text-ws-ink placeholder:text-ws-ink-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent';

  const submitRequest = async (e: FormEvent) => {
    e.preventDefault();
    setReqError(null);
    if (!STUDENT_ID_RE.test(reqStudentId.trim())) {
      setReqError('Student ID must be your 9-digit college ID.');
      return;
    }
    setReqBusy(true);
    try {
      const { email_configured } = await requestReset(reqStudentId.trim());
      setEmailConfigured(email_configured);
      setStudentId(reqStudentId.trim());
      setView('requested');
    } catch {
      // Shape errors only (400 invalid_body) — the route otherwise always 202s.
      setReqError('Please check your student ID and try again.');
    } finally {
      setReqBusy(false);
    }
  };

  const submitConfirm = async (e: FormEvent) => {
    e.preventDefault();
    setConfirmError(null);
    if (!STUDENT_ID_RE.test(studentId.trim())) {
      setConfirmError('Student ID must be your 9-digit college ID.');
      return;
    }
    if (!code.trim()) {
      setConfirmError('Please enter your reset code.');
      return;
    }
    if (newPassword.length < 10) {
      setConfirmError('New password must be at least 10 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setConfirmError('New passwords do not match.');
      return;
    }
    setConfirmBusy(true);
    try {
      await confirmReset(studentId.trim(), code.trim(), newPassword);
      setView('done');
    } catch (err) {
      setConfirmError(messageForConfirmError(err));
    } finally {
      setConfirmBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-ws-bg px-4 py-8">
      <Card className="w-full max-w-sm p-8">
        <h1 className="text-xl font-semibold text-ws-ink">Reset your password</h1>

        {view === 'start' && (
          <>
            <p className="mt-1.5 text-sm text-ws-ink-3">
              Ask your instructor for a reset code in class, or request an emailed one below.
            </p>
            <form onSubmit={submitRequest} className="mt-5 flex flex-col gap-3">
              {reqError && (
                <div role="alert" aria-invalid="true" className="rounded-md border border-ws-danger bg-ws-surface-2 px-3 py-2 text-sm text-ws-danger">
                  {reqError}
                </div>
              )}
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-ws-ink-2">Student ID</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={reqStudentId}
                  onChange={(e) => setReqStudentId(e.target.value.replace(/[^\d]/g, '').slice(0, 9))}
                  placeholder="123456789"
                  maxLength={9}
                  className={inputClass}
                  required
                />
              </label>
              <Button type="submit" size="lg" loading={reqBusy} className="w-full mt-1">
                Email me a reset code
              </Button>
            </form>
            <p className="mt-4 text-center text-sm text-ws-ink-3">
              Already have a code?{' '}
              <button
                type="button"
                onClick={() => setView('confirm')}
                className="font-medium text-ws-accent hover:text-ws-accent-ink underline"
              >
                Enter it here
              </button>
            </p>
          </>
        )}

        {view === 'requested' && (
          <>
            {emailConfigured ? (
              <p className="mt-1.5 text-sm text-ws-ink-3">
                If that student ID has an email on file, a reset link was just sent to it. Check your
                inbox, or ask your instructor for a code instead.
              </p>
            ) : (
              // WIP-002 — email delivery is not configured right now, so no
              // link was actually sent. Don't say otherwise.
              <p className="mt-1.5 text-sm text-ws-ink-3">
                Email delivery isn't set up yet, so no reset link was sent. Ask your instructor for a
                reset code in class instead.
              </p>
            )}
            <Button variant="secondary" className="mt-5 w-full" onClick={() => setView('confirm')}>
              I have a code
            </Button>
          </>
        )}

        {view === 'confirm' && (
          <>
            <p className="mt-1.5 text-sm text-ws-ink-3">
              Enter your student ID, the reset code, and a new password.
            </p>
            <form onSubmit={submitConfirm} className="mt-5 flex flex-col gap-3">
              {confirmError && (
                <div role="alert" aria-invalid="true" className="rounded-md border border-ws-danger bg-ws-surface-2 px-3 py-2 text-sm text-ws-danger">
                  {confirmError}
                </div>
              )}
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-ws-ink-2">Student ID</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value.replace(/[^\d]/g, '').slice(0, 9))}
                  placeholder="123456789"
                  maxLength={9}
                  className={inputClass}
                  required
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-ws-ink-2">Reset code</span>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="xxxx-xxxx"
                  className={inputClass}
                  required
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-ws-ink-2">New password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 10 characters"
                  minLength={10}
                  className={inputClass}
                  required
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-ws-ink-2">Confirm new password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputClass}
                  required
                />
              </label>
              <Button type="submit" size="lg" loading={confirmBusy} className="w-full mt-1">
                Set new password
              </Button>
            </form>
          </>
        )}

        {view === 'done' && (
          <>
            <div role="status" className="mt-5 rounded-md border border-ws-success bg-ws-success-quiet px-3 py-2 text-sm text-ws-success">
              Your password has been reset.
            </div>
            <Link to="/login">
              <Button className="mt-5 w-full">Sign in</Button>
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}
